/**
 * The review panel for an UNAPPLIED Scope AI refinement proposal.
 *
 * Purely presentational: it renders what the presenter established and calls
 * back on dismiss. It performs no request and mutates nothing — a proposal
 * is reviewed here and then acted on (or not) through the governed activity
 * editing routes until a dedicated refinement-apply workflow exists.
 */
import { Button } from "../../components/ui/button";
import { NOT_APPLIED_LABEL } from "./refineProposalPresentation";

const ItemFacts = ({ label, item, testId }) => (
  <div className="flex-1 min-w-0 space-y-1" data-testid={testId}>
    <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="font-medium truncate">{item.name}</p>
    {item.duration_days != null && (
      <p className="text-sm text-muted-foreground">{item.duration_days} day(s)</p>
    )}
    {item.description ? (
      <p className="text-sm text-muted-foreground">{item.description}</p>
    ) : null}
    {Array.isArray(item.tasks) && item.tasks.length > 0 && (
      <ul className="text-sm text-muted-foreground list-disc pl-4">
        {item.tasks.map((task, index) => (
          <li key={`${task.name || "task"}-${index}`}>
            {task.name}
            {task.duration_days != null ? ` — ${task.duration_days} day(s)` : ""}
          </li>
        ))}
      </ul>
    )}
  </div>
);

export const RefineProposalReview = ({ proposal, onDismiss }) => {
  if (!proposal) return null;
  return (
    <div
      className="p-4 border-b border-primary/30 bg-primary/5 space-y-4"
      data-testid="refine-proposal-review"
      role="region"
      aria-label="AI refinement proposal, not applied"
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="font-semibold">AI refinement proposal</p>
          <p
            className="text-xs font-semibold text-amber-400"
            data-testid="refine-proposal-state"
          >
            {NOT_APPLIED_LABEL}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={onDismiss} data-testid="refine-proposal-dismiss">
          Close without applying
        </Button>
      </div>

      {proposal.items.map((item) => (
        <div
          key={item.fingerprint}
          className="rounded border border-border p-3 space-y-2"
          data-testid={`refine-proposal-item-${item.itemName}`}
        >
          <div className="flex flex-wrap gap-4">
            <ItemFacts label="Current" item={item.current} testId="refine-proposal-current" />
            <ItemFacts label="Proposed" item={item.proposed} testId="refine-proposal-proposed" />
          </div>
          {item.summary ? (
            <p className="text-sm" data-testid="refine-proposal-summary">{item.summary}</p>
          ) : null}
        </div>
      ))}

      {proposal.warnings.map((warning, index) => (
        <p key={index} className="text-sm text-amber-400" data-testid="refine-proposal-warning">
          {warning}
        </p>
      ))}

      <p className="text-xs text-muted-foreground">
        Nothing has been changed. To act on this proposal, edit the plan
        through the activity editor; a governed apply workflow will follow.
      </p>
    </div>
  );
};
